import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, ChevronRight, Upload, Sparkles, Loader2, Car, FileText, ShieldCheck, Gauge } from "lucide-react";
import { useAuth, roleHomePath } from "@/contexts/AuthContext";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import {
  TextField, INRField, MobileField, DOBField, StateField, PinField, PANField, SelectField,
  isPAN, isPIN, isMobile, isEmail, isVIN, INDIAN_STATES,
} from "@/components/form/IndianFields";

const STEPS = ["Vehicle", "Loan Terms", "Personal", "Income", "Review"];
const FUEL = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "LPG"];
const RES = ["Owned", "Rented", "Family-owned", "Company-provided", "Paying Guest"];
const EMP = [
  { value: "Salaried", label: "Salaried" },
  { value: "Self-Employed Professional", label: "Self-Employed Professional" },
  { value: "Self-Employed Business", label: "Self-Employed Business" },
  { value: "Retired", label: "Retired" },
  { value: "Pensioner", label: "Pensioner" },
  { value: "Agriculturist", label: "Agriculturist" },
];
const TENURES = [12, 24, 36, 48, 60, 72, 84];
const RELATIONS = ["Father", "Mother", "Spouse", "Brother", "Sister", "Son", "Daughter", "Friend", "Colleague"];

export default function Apply({ embedded = false, source = "borrower" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [errors, setErrors] = useState({});
  const pdfRef = useRef();

  const [form, setForm] = useState({
    // Step 1 — Vehicle (CarDr-driven)
    vehicle: {
      make: "", model: "", variant: "", year: "", fuel_type: "Petrol",
      km_driven: 0, ownership_count: 1, registration_number: "", vin: "",
      seller_name: "",
    },
    cardr_uploaded: false,
    cardr_filename: null,
    cardr_data: null,
    market_estimate: null,

    // Step 2 — Loan Terms
    loan: {
      vehicle_price: 0,
      desired_loan_amount: 0,
      down_payment: 0,
      tenure_months: 60,
      loan_purpose: "Used Car Purchase",
    },

    // Step 3 — Personal
    borrower: {
      full_name: user?.name || "",
      mobile: (user?.phone || "").replace(/\D/g, "").slice(-10) || "",
      email: user?.email || "",
      dob: "",
      pan: "",
      current_address: "",
      city: "",
      state: "",
      pincode: "",
      residence_ownership: "Owned",
      years_at_residence: 2,
      marital_status: "Single",
      father_name: "",
    },

    // Step 4 — Income
    employment: {
      employment_type: "Salaried",
      employer_name: "",
      designation: "",
      years_in_current_job: 2,
      total_work_experience: 5,
      monthly_income: 0,
      other_income: 0,
      family_income: 0,
      existing_emi: 0,
      dependents: 1,
      cibil_score: 720,
      primary_bank: "",
    },

    // References (lightweight, 2 max)
    references: [
      { name: "", relationship: "Father", mobile: "" },
      { name: "", relationship: "Friend", mobile: "" },
    ],
  });

  const setSection = (section, k, v) => setForm((p) => ({ ...p, [section]: { ...p[section], [k]: v } }));
  const setRef = (idx, k, v) => setForm((p) => {
    const next = [...p.references]; next[idx] = { ...next[idx], [k]: v }; return { ...p, references: next };
  });

  // --- CarDr preview extract ---
  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { toast.error("Please sign in to upload a CarDr report."); return; }
    setExtracting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/cardr/preview-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      // Merge into form
      setForm((p) => ({
        ...p,
        vehicle: { ...p.vehicle, ...data.vehicle, seller_name: p.vehicle.seller_name },
        cardr_uploaded: true,
        cardr_filename: data.pdf_filename || file.name,
        cardr_data: data.cardr,
      }));
      toast.success(`CarDr extracted — Health Grade ${data.cardr?.health_grade || "?"}, ${data.vehicle.make} ${data.vehicle.model}`);
      // Auto-fire market estimate so the borrower sees a price range on the same step
      if (data.vehicle?.make && data.vehicle?.model && data.vehicle?.year) {
        fetchMarketEstimate({ ...data.vehicle, health_grade: data.cardr?.health_grade });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed. You can still enter details manually.");
    } finally {
      setExtracting(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  // --- Market estimate (Gemini knowledge-based) ---
  const fetchMarketEstimate = async (overrides = {}) => {
    const v = { ...form.vehicle, ...overrides };
    if (!user) { toast.error("Please sign in to fetch market estimate."); return; }
    if (!v.make || !v.model || !v.year) {
      toast.error("Make, model and year are required");
      return;
    }
    setEstimating(true);
    try {
      const { data } = await api.post("/valuation/market-estimate", {
        make: v.make,
        model: v.model,
        year: Number(v.year),
        km_driven: Number(v.km_driven) || 0,
        variant: v.variant || "",
        fuel_type: v.fuel_type || "Petrol",
        ownership_count: Number(v.ownership_count) || 1,
        city: form.borrower.city || "Delhi",
        health_grade: overrides.health_grade || form.cardr_data?.health_grade || "B",
      }, { timeout: 90000 });
      setForm((p) => ({
        ...p,
        market_estimate: data,
        // Pre-fill vehicle_price with the mid_inr so the Loan Terms step starts from a sensible default
        loan: { ...p.loan, vehicle_price: Number(data.mid_inr) || p.loan.vehicle_price },
      }));
      toast.success(`Market range: ${formatINR(data.low_inr)} – ${formatINR(data.high_inr)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Market estimate failed");
    } finally { setEstimating(false); }
  };

  // --- Validation per step ---
  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      const v = form.vehicle;
      if (!v.make) e["vehicle.make"] = "Required";
      if (!v.model) e["vehicle.model"] = "Required";
      if (!v.year || Number(v.year) < 1990 || Number(v.year) > new Date().getFullYear()) e["vehicle.year"] = "Enter a valid year";
      if (!v.fuel_type) e["vehicle.fuel_type"] = "Required";
      if (v.km_driven == null || Number(v.km_driven) < 0) e["vehicle.km_driven"] = "Enter kilometers";
      if (v.vin && !isVIN(v.vin)) e["vehicle.vin"] = "VIN should be 11–17 alphanumeric chars (no I/O/Q)";
      if (!v.registration_number) e["vehicle.registration_number"] = "Registration number required";
    }
    if (s === 1) {
      const l = form.loan;
      if (!l.vehicle_price || l.vehicle_price < 50000) e["loan.vehicle_price"] = "Enter vehicle price (min ₹50,000)";
      if (!l.desired_loan_amount || l.desired_loan_amount < 50000) e["loan.desired_loan_amount"] = "Enter loan amount (min ₹50,000)";
      if (l.desired_loan_amount > l.vehicle_price) e["loan.desired_loan_amount"] = "Loan cannot exceed vehicle price";
      if (l.down_payment < 0 || l.down_payment > l.vehicle_price) e["loan.down_payment"] = "Invalid down payment";
    }
    if (s === 2) {
      const b = form.borrower;
      if (!b.full_name) e["borrower.full_name"] = "Required";
      if (!isMobile(b.mobile)) e["borrower.mobile"] = "Enter a valid 10-digit Indian mobile";
      if (b.email && !isEmail(b.email)) e["borrower.email"] = "Invalid email";
      if (!b.dob) e["borrower.dob"] = "Required";
      if (!isPAN(b.pan)) e["borrower.pan"] = "PAN format: ABCDE1234F";
      if (!b.current_address) e["borrower.current_address"] = "Required";
      if (!b.city) e["borrower.city"] = "Required";
      if (!b.state) e["borrower.state"] = "Required";
      if (!isPIN(b.pincode)) e["borrower.pincode"] = "Enter 6-digit PIN";
    }
    if (s === 3) {
      const m = form.employment;
      if (!m.employment_type) e["employment.employment_type"] = "Required";
      if (!m.employer_name) e["employment.employer_name"] = "Required";
      if (!m.monthly_income || m.monthly_income < 10000) e["employment.monthly_income"] = "Enter monthly income (min ₹10,000)";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) { toast.error("Please fix the highlighted fields"); return; }
    // Auto-compute down payment when leaving step 2
    if (step === 1) {
      const dp = (form.loan.vehicle_price || 0) - (form.loan.desired_loan_amount || 0);
      setSection("loan", "down_payment", Math.max(0, dp));
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  // --- Indicative valuation preview ---
  const indicative = useMemo(() => {
    const price = Number(form.loan.vehicle_price) || 0;
    const grade = form.cardr_data?.health_grade || "B";
    const repair = Number(form.cardr_data?.repair_estimate) || 0;
    const year = Number(form.vehicle.year) || new Date().getFullYear();
    const age = Math.max(0, new Date().getFullYear() - year);
    const km = Number(form.vehicle.km_driven) || 0;
    const ageDisc = Math.min(0.4, age * 0.04);
    const kmDisc = Math.min(0.15, (km / 150000) * 0.15);
    const diagDisc = { A: 0, B: 0.03, C: 0.08, D: 0.18, Reject: 1.0 }[grade] ?? 0.05;
    const liqDisc = 0.05;
    const lendable = Math.max(0, price - repair - price * (ageDisc + kmDisc + diagDisc + liqDisc));
    const ltvMax = { A: 0.80, B: 0.75, C: 0.65, D: 0.50, Reject: 0 }[grade] ?? 0.65;
    const recommendedLoan = lendable * ltvMax;
    return { lendable: Math.round(lendable), ltvMax, recommendedLoan: Math.round(recommendedLoan), grade };
  }, [form.loan.vehicle_price, form.cardr_data, form.vehicle.year, form.vehicle.km_driven]);

  // --- Submit ---
  const submit = async () => {
    if (!user) { toast.error("Please sign in as a borrower to submit."); navigate("/login"); return; }
    setSubmitting(true);
    try {
      const payload = {
        borrower: {
          ...form.borrower,
          mobile: `+91${form.borrower.mobile}`,
          monthly_income: Number(form.employment.monthly_income),
          family_income: Number(form.employment.family_income),
          existing_emi: Number(form.employment.existing_emi),
          dependents: Number(form.employment.dependents),
          cibil_score: Number(form.employment.cibil_score),
          employment_type: form.employment.employment_type,
          employer_name: form.employment.employer_name,
          years_at_residence: Number(form.borrower.years_at_residence),
        },
        vehicle: {
          ...form.vehicle,
          year: Number(form.vehicle.year),
          km_driven: Number(form.vehicle.km_driven),
          ownership_count: Number(form.vehicle.ownership_count),
          vehicle_price: Number(form.loan.vehicle_price),
          market_value: Number(form.loan.vehicle_price),
        },
        loan: {
          desired_loan_amount: Number(form.loan.desired_loan_amount),
          down_payment: Number(form.loan.down_payment),
          tenure_months: Number(form.loan.tenure_months),
          loan_purpose: form.loan.loan_purpose,
        },
        source,
      };
      const { data } = await api.post("/applications", payload);
      // If CarDr was extracted, attach it
      if (form.cardr_data) {
        try { await api.post(`/applications/${data.id}/cardr/extract`, form.cardr_data); } catch (_e) {}
      }
      toast.success(`Application ${data.application_id} created`);
      const base = roleHomePath(user.role);
      navigate(`${base}/applications/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  // ---------- Render ----------
  const content = (
    <div className="max-w-4xl mx-auto" data-testid="apply-page">
      {!embedded && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold">Loan application</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Apply for a used car loan</h1>
          <p className="text-sm text-slate-600 mt-1">5-step guided flow. Start with the vehicle — upload a CarDr report and we'll pre-fill everything.</p>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 overflow-x-auto pb-4" data-testid="stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 min-w-fit">
            <div
              data-testid={`step-indicator-${i}`}
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold flex-shrink-0 ${
                i < step ? "bg-teal-600 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >{i < step ? <Check size={14} /> : i + 1}</div>
            <div className={`text-sm font-medium ${i === step ? "text-slate-900" : "text-slate-500"}`}>{s}</div>
            {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </div>
        ))}
      </div>

      <Card className="p-6 border-slate-200">
        {step === 0 && (
          <VehicleStep
            form={form} setSection={setSection} errors={errors}
            onPdfPick={() => pdfRef.current?.click()} pdfRef={pdfRef} onPdf={onPdf}
            extracting={extracting} cardr={form.cardr_data}
            marketEstimate={form.market_estimate} estimating={estimating}
            onFetchEstimate={fetchMarketEstimate}
          />
        )}
        {step === 1 && (
          <LoanStep form={form} setSection={setSection} errors={errors} indicative={indicative} />
        )}
        {step === 2 && (
          <PersonalStep form={form} setSection={setSection} errors={errors} />
        )}
        {step === 3 && (
          <IncomeStep form={form} setSection={setSection} setRef={setRef} errors={errors} />
        )}
        {step === 4 && (
          <ReviewStep form={form} indicative={indicative} />
        )}

        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="apply-back-btn">Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="bg-slate-900 hover:bg-slate-800" data-testid="apply-next-btn">Continue</Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="apply-submit-btn">
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
          )}
        </div>
      </Card>

      {!user && (
        <div className="mt-4 text-sm text-slate-600">
          You'll need to <a className="text-teal-700 font-medium" href="/login">sign in</a> as a borrower (use <span className="font-mono">borrower@carfin.com / demo123</span>) to submit this application.
        </div>
      )}
    </div>
  );

  return embedded ? content : <div className="px-4 lg:px-8 py-12">{content}</div>;
}

// ============================================================
// STEP 1 — Vehicle (CarDr-driven OR manual)
// ============================================================
function VehicleStep({ form, setSection, errors, onPdfPick, pdfRef, onPdf, extracting, cardr, marketEstimate, estimating, onFetchEstimate }) {
  const [mode, setMode] = useState(form.cardr_uploaded ? "upload" : "upload");
  const v = form.vehicle;
  const canEstimate = !!(v.make && v.model && v.year);

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Car size={20} /> Vehicle details</h2>
        <p className="text-sm text-slate-600 mt-1">Upload a CarDr diagnostic report and we'll auto-fill make, model, VIN, registration, odometer and health grade. Or enter details manually.</p>
      </div>

      <Tabs value={mode} onValueChange={setMode} className="mb-5">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="upload" data-testid="tab-cardr-upload"><Sparkles size={14} className="mr-1" /> Upload CarDr report</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual-vehicle">Enter manually</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-4">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50/50">
            <input ref={pdfRef} type="file" accept="application/pdf" hidden onChange={onPdf} data-testid="cardr-pdf-input" />
            {extracting ? (
              <div className="flex flex-col items-center gap-3 py-4" data-testid="cardr-extracting">
                <Loader2 className="animate-spin text-teal-600" size={32} />
                <div className="text-sm font-medium text-slate-700">Reading your CarDr report…</div>
                <div className="text-xs text-slate-500">This may take 30–60 seconds.</div>
              </div>
            ) : form.cardr_uploaded ? (
              <div className="py-4" data-testid="cardr-extracted-summary">
                <div className="flex items-center justify-center gap-2 text-teal-700 font-semibold">
                  <FileText size={20} /> {form.cardr_filename}
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm max-w-2xl mx-auto text-left">
                  <Pill label="Health Grade" value={cardr?.health_grade} highlight />
                  <Pill label="Health Score" value={`${cardr?.vehicle_health_score || 0}/100`} />
                  <Pill label="Active DTCs" value={cardr?.current_dtc_count} />
                  <Pill label="Repair Est." value={formatINR(cardr?.repair_estimate)} />
                </div>
                <Button variant="outline" onClick={onPdfPick} className="mt-4" data-testid="cardr-reupload-btn">Upload a different report</Button>
              </div>
            ) : (
              <>
                <Upload size={28} className="text-slate-400 mx-auto" />
                <div className="text-base font-medium text-slate-700 mt-3">Drop a CarDr scan PDF here</div>
                <div className="text-xs text-slate-500 mt-1">We'll extract VIN, registration, make, model, variant, odometer and diagnostic health grade.</div>
                <Button onClick={onPdfPick} className="mt-4 bg-teal-600 hover:bg-teal-700" data-testid="cardr-upload-btn">
                  <Upload size={16} className="mr-1" /> Choose PDF
                </Button>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="manual" className="mt-4">
          <div className="text-xs text-slate-500 mb-3">All fields below — extracted from CarDr in upload mode.</div>
        </TabsContent>
      </Tabs>

      {/* Vehicle fields (always editable — even when extracted) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField id="vehicle-make" testId="input-make" label="Make" value={form.vehicle.make} onChange={(v) => setSection("vehicle", "make", v)} error={errors["vehicle.make"]} placeholder="Maruti, Hyundai, Tata…" />
        <TextField id="vehicle-model" testId="input-model" label="Model" value={form.vehicle.model} onChange={(v) => setSection("vehicle", "model", v)} error={errors["vehicle.model"]} placeholder="Swift, Creta…" />
        <TextField id="vehicle-variant" testId="input-variant" label="Variant" value={form.vehicle.variant} onChange={(v) => setSection("vehicle", "variant", v)} placeholder="VXI, SX(O)…" />
        <TextField id="vehicle-year" testId="input-year" type="number" label="Year" value={form.vehicle.year} onChange={(v) => setSection("vehicle", "year", v)} error={errors["vehicle.year"]} placeholder="2020" />
        <SelectField id="vehicle-fuel" testId="select-fuel" label="Fuel type" value={form.vehicle.fuel_type} onChange={(v) => setSection("vehicle", "fuel_type", v)} options={FUEL} error={errors["vehicle.fuel_type"]} />
        <TextField id="vehicle-km" testId="input-km" type="number" label="Kilometers driven" value={form.vehicle.km_driven} onChange={(v) => setSection("vehicle", "km_driven", v)} error={errors["vehicle.km_driven"]} placeholder="42000" />
        <SelectField id="vehicle-owners" testId="select-owners" label="Number of owners" value={String(form.vehicle.ownership_count)} onChange={(v) => setSection("vehicle", "ownership_count", Number(v))} options={["1", "2", "3", "4", "5"]} />
        <TextField id="vehicle-reg" testId="input-reg" label="Registration number" value={form.vehicle.registration_number} onChange={(v) => setSection("vehicle", "registration_number", v.toUpperCase())} error={errors["vehicle.registration_number"]} placeholder="MH02CV1234" />
        <TextField id="vehicle-vin" testId="input-vin" label="VIN / Chassis number" value={form.vehicle.vin} onChange={(v) => setSection("vehicle", "vin", v.toUpperCase())} error={errors["vehicle.vin"]} placeholder="11–17 chars" hint="As shown on the chassis plate / RC" />
        <TextField id="vehicle-seller" testId="input-seller" label="Dealer / Seller name" value={form.vehicle.seller_name} onChange={(v) => setSection("vehicle", "seller_name", v)} placeholder="From whom are you buying?" />
      </div>

      {/* ---------- Market Estimate (Spinny / Cars24 / CarAndBike via Gemini) ---------- */}
      <div className="mt-6 p-5 rounded-lg border border-teal-200 bg-gradient-to-br from-teal-50/60 to-white" data-testid="apply-market-estimate">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-base font-semibold text-slate-900 flex items-center gap-2"><Sparkles size={16} className="text-teal-600" /> Indicative market price range</div>
            <div className="text-sm text-slate-600">Pulled from Spinny / Cars24 / CarAndBike-style listings. We'll pre-fill vehicle price on the next step.</div>
          </div>
          <Button
            onClick={() => onFetchEstimate({})}
            disabled={!canEstimate || estimating}
            className="bg-slate-900 hover:bg-slate-800 shrink-0"
            data-testid="apply-market-estimate-btn"
          >
            {estimating ? <><Loader2 size={14} className="mr-1 animate-spin" /> Estimating…</> : marketEstimate ? "Refresh" : "Get estimate"}
          </Button>
        </div>
        {!canEstimate ? (
          <div className="text-sm text-slate-500">Fill in make, model and year above to get a market range.</div>
        ) : !marketEstimate ? (
          <div className="text-sm text-slate-500">No estimate yet — click <span className="font-medium">Get estimate</span> above.</div>
        ) : (
          <div className="space-y-3" data-testid="apply-market-estimate-result">
            <div className="grid grid-cols-3 gap-3">
              <ApplyRangeStat label="Low" value={marketEstimate.low_inr} tone="rose" />
              <ApplyRangeStat label="Mid" value={marketEstimate.mid_inr} tone="teal" highlight />
              <ApplyRangeStat label="High" value={marketEstimate.high_inr} tone="emerald" />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="px-2 py-0.5 rounded-full bg-slate-100 font-mono uppercase tracking-wider">{marketEstimate.confidence || "medium"} confidence</span>
              <span>Sources: {(marketEstimate.sources || []).join(" · ")}</span>
            </div>
            {marketEstimate.source_notes && <div className="text-sm text-slate-700">{marketEstimate.source_notes}</div>}
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              ⚠ {marketEstimate.knowledge_cutoff_disclaimer}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApplyRangeStat({ label, value, tone, highlight }) {
  const toneClass = {
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    teal: "border-teal-200 bg-teal-50 text-teal-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-900";
  return (
    <div className={`rounded-lg border ${toneClass} px-3 py-2`} data-testid={`apply-market-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className={`mt-1 font-mono ${highlight ? "text-xl font-bold" : "text-base font-semibold"}`}>{formatINR(value)}</div>
    </div>
  );
}

function Pill({ label, value, highlight }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-mono font-semibold ${highlight ? "text-teal-700" : "text-slate-900"}`}>{value ?? "—"}</div>
    </div>
  );
}

// ============================================================
// STEP 2 — Loan Terms (upfront)
// ============================================================
function LoanStep({ form, setSection, errors, indicative }) {
  const price = form.loan.vehicle_price || 0;
  const loan = form.loan.desired_loan_amount || 0;
  const dp = Math.max(0, price - loan);
  const ltvPct = price > 0 ? Math.round((loan / price) * 100) : 0;

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2 mb-1"><Gauge size={20} /> Loan terms</h2>
      <p className="text-sm text-slate-600 mb-5">Tell us the purchase price and how much loan you need. We'll work out the down payment.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <INRField id="loan-price" testId="inr-vehicle-price" label="Target purchase price" value={form.loan.vehicle_price} onChange={(v) => setSection("loan", "vehicle_price", v)} error={errors["loan.vehicle_price"]} placeholder="6,50,000" hint="What you're paying the seller" />
        <INRField id="loan-amount" testId="inr-loan-amount" label="Loan amount required" value={form.loan.desired_loan_amount} onChange={(v) => { setSection("loan", "desired_loan_amount", v); setSection("loan", "down_payment", Math.max(0, (form.loan.vehicle_price || 0) - v)); }} error={errors["loan.desired_loan_amount"]} placeholder="5,00,000" />
        <INRField id="loan-dp" testId="inr-down-payment" label="Down payment (auto)" value={dp} onChange={(v) => setSection("loan", "down_payment", v)} error={errors["loan.down_payment"]} hint="Updated automatically" />
        <div>
          <Label>Indicative LTV</Label>
          <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 font-mono">
            <span className={ltvPct > 80 ? "text-amber-700" : "text-slate-900"}>{ltvPct}%</span>
            <span className="text-xs text-slate-500 ml-3">{ltvPct > 80 ? "High — may need higher down payment" : ltvPct > 0 ? "Within typical policy" : "—"}</span>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex justify-between items-baseline mb-2">
          <Label>Tenure</Label>
          <div className="text-sm font-mono font-semibold text-teal-700" data-testid="tenure-display">{form.loan.tenure_months} months</div>
        </div>
        <Slider
          value={[form.loan.tenure_months]}
          min={12} max={84} step={12}
          onValueChange={(v) => setSection("loan", "tenure_months", v[0])}
          data-testid="tenure-slider"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
          {TENURES.map((t) => <span key={t}>{t}m</span>)}
        </div>
      </div>

      {/* Indicative valuation card */}
      {price > 0 && (
        <Card className="p-4 mt-6 bg-teal-50 border-teal-200" data-testid="indicative-valuation">
          <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2 flex items-center gap-2"><Sparkles size={14} /> Indicative loan offer (before underwriting)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Pill label="CarDr Grade" value={indicative.grade} highlight />
            <Pill label="Max LTV" value={`${Math.round(indicative.ltvMax * 100)}%`} />
            <Pill label="Lendable value" value={formatINR(indicative.lendable)} />
            <Pill label="Indicative loan" value={formatINR(indicative.recommendedLoan)} highlight />
          </div>
          <div className="text-xs text-slate-600 mt-3">
            Final terms are subject to Red Fort Capital credit policy, vehicle valuation, document verification and underwriting approval.
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// STEP 3 — Personal
// ============================================================
function PersonalStep({ form, setSection, errors }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Personal details</h2>
      <p className="text-sm text-slate-600 mb-5">Tell us about yourself. All fields are required unless marked optional.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField id="b-name" testId="input-full_name" label="Full name (as per PAN)" value={form.borrower.full_name} onChange={(v) => setSection("borrower", "full_name", v)} error={errors["borrower.full_name"]} />
        <TextField id="b-father" label="Father's name" value={form.borrower.father_name} onChange={(v) => setSection("borrower", "father_name", v)} placeholder="Optional" />
        <MobileField id="b-mobile" label="Mobile" value={form.borrower.mobile} onChange={(v) => setSection("borrower", "mobile", v)} error={errors["borrower.mobile"]} testId="input-mobile" />
        <TextField id="b-email" testId="input-email" label="Email" type="email" value={form.borrower.email} onChange={(v) => setSection("borrower", "email", v)} error={errors["borrower.email"]} />
        <DOBField id="b-dob" label="Date of birth (dd/mm/yyyy)" value={form.borrower.dob} onChange={(v) => setSection("borrower", "dob", v)} minAge={18} error={errors["borrower.dob"]} testId="input-dob" />
        <SelectField id="b-marital" label="Marital status" value={form.borrower.marital_status} onChange={(v) => setSection("borrower", "marital_status", v)} options={["Single", "Married", "Divorced", "Widowed"]} testId="select-marital" />
        <PANField id="b-pan" label="PAN" value={form.borrower.pan} onChange={(v) => setSection("borrower", "pan", v)} error={errors["borrower.pan"]} testId="input-pan" />
        <SelectField id="b-residence" label="Residence ownership" value={form.borrower.residence_ownership} onChange={(v) => setSection("borrower", "residence_ownership", v)} options={RES} testId="select-residence" />
        <TextField id="b-yrs" testId="input-years-residence" type="number" label="Years at current address" value={form.borrower.years_at_residence} onChange={(v) => setSection("borrower", "years_at_residence", v)} />
        <TextField id="b-addr" testId="input-address" label="Current address" value={form.borrower.current_address} onChange={(v) => setSection("borrower", "current_address", v)} error={errors["borrower.current_address"]} className="md:col-span-2" placeholder="House/flat, street, locality" />
        <TextField id="b-city" testId="input-city" label="City" value={form.borrower.city} onChange={(v) => setSection("borrower", "city", v)} error={errors["borrower.city"]} />
        <StateField id="b-state" value={form.borrower.state} onChange={(v) => setSection("borrower", "state", v)} error={errors["borrower.state"]} testId="select-state" />
        <PinField id="b-pin" value={form.borrower.pincode} onChange={(v) => setSection("borrower", "pincode", v)} error={errors["borrower.pincode"]} testId="input-pincode" />
      </div>
    </div>
  );
}

// ============================================================
// STEP 4 — Income
// ============================================================
function IncomeStep({ form, setSection, setRef, errors }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Income & employment</h2>
      <p className="text-sm text-slate-600 mb-5">Helps us determine your eligibility (FOIR, affordability and interest rate).</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField id="e-type" label="Employment type" value={form.employment.employment_type} onChange={(v) => setSection("employment", "employment_type", v)} options={EMP} testId="select-employment" />
        <TextField id="e-employer" testId="input-employer" label="Employer / Business name" value={form.employment.employer_name} onChange={(v) => setSection("employment", "employer_name", v)} error={errors["employment.employer_name"]} />
        <TextField id="e-designation" label="Designation / Role" value={form.employment.designation} onChange={(v) => setSection("employment", "designation", v)} placeholder="Optional" />
        <TextField id="e-curr-yrs" testId="input-curr-yrs" type="number" label="Years in current job" value={form.employment.years_in_current_job} onChange={(v) => setSection("employment", "years_in_current_job", v)} />
        <TextField id="e-total-yrs" testId="input-total-exp" type="number" label="Total work experience (years)" value={form.employment.total_work_experience} onChange={(v) => setSection("employment", "total_work_experience", v)} />
        <TextField id="e-bank" label="Primary salary / business bank" value={form.employment.primary_bank} onChange={(v) => setSection("employment", "primary_bank", v)} placeholder="HDFC Bank / SBI / …" />

        <INRField id="e-income" testId="inr-monthly-income" label="Monthly take-home income" value={form.employment.monthly_income} onChange={(v) => setSection("employment", "monthly_income", v)} error={errors["employment.monthly_income"]} placeholder="75,000" />
        <INRField id="e-other" label="Other monthly income" value={form.employment.other_income} onChange={(v) => setSection("employment", "other_income", v)} placeholder="0" hint="Rent, freelance, etc." />
        <INRField id="e-family" testId="inr-family-income" label="Total family income" value={form.employment.family_income} onChange={(v) => setSection("employment", "family_income", v)} placeholder="1,20,000" />
        <INRField id="e-emi" testId="inr-existing-emi" label="Existing EMIs (total)" value={form.employment.existing_emi} onChange={(v) => setSection("employment", "existing_emi", v)} placeholder="0" hint="Sum of all current loan EMIs" />
        <TextField id="e-deps" testId="input-deps" type="number" label="Dependents" value={form.employment.dependents} onChange={(v) => setSection("employment", "dependents", v)} />
        <TextField id="e-cibil" testId="input-cibil" type="number" label="CIBIL score (if known)" value={form.employment.cibil_score} onChange={(v) => setSection("employment", "cibil_score", v)} hint="300–900. We'll pull it via consent during underwriting." />
      </div>

      <div className="mt-8">
        <div className="text-sm font-semibold text-slate-900 mb-3">References (2 contacts who can vouch for you)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {form.references.map((ref, i) => (
            <Card key={i} className="p-4 border-slate-200 space-y-3 md:col-span-1">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reference {i + 1}</div>
              <TextField id={`ref-name-${i}`} label="Name" value={ref.name} onChange={(v) => setRef(i, "name", v)} />
              <SelectField id={`ref-rel-${i}`} label="Relationship" value={ref.relationship} onChange={(v) => setRef(i, "relationship", v)} options={RELATIONS} />
              <MobileField id={`ref-mob-${i}`} label="Mobile" value={ref.mobile} onChange={(v) => setRef(i, "mobile", v)} />
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEP 5 — Review
// ============================================================
function ReviewStep({ form, indicative }) {
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  return (
    <div data-testid="review-step">
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Review & submit</h2>
      <p className="text-sm text-slate-600 mb-5">Please check the details below.</p>

      <ReviewBlock title="Vehicle" data={{
        "Vehicle": `${form.vehicle.make} ${form.vehicle.model} ${form.vehicle.variant || ""}`.trim(),
        "Year / Fuel": `${form.vehicle.year || "—"} · ${form.vehicle.fuel_type}`,
        "Reg / VIN": `${form.vehicle.registration_number || "—"} · ${form.vehicle.vin || "—"}`,
        "Kilometers / Owners": `${(form.vehicle.km_driven || 0).toLocaleString("en-IN")} km · ${form.vehicle.ownership_count}`,
        "Seller": form.vehicle.seller_name || "—",
        "CarDr report": form.cardr_uploaded ? `${form.cardr_filename} · Grade ${form.cardr_data?.health_grade}` : "Not uploaded (manual entry)",
      }} />
      <ReviewBlock title="Loan terms" data={{
        "Vehicle price": formatINR(form.loan.vehicle_price),
        "Loan amount": formatINR(form.loan.desired_loan_amount),
        "Down payment": formatINR(form.loan.down_payment),
        "Tenure": `${form.loan.tenure_months} months`,
        "Indicative offer": `${formatINR(indicative.recommendedLoan)} @ Grade ${indicative.grade}`,
      }} />
      <ReviewBlock title="Personal" data={{
        "Name": form.borrower.full_name,
        "Mobile": `+91 ${form.borrower.mobile}`,
        "Email": form.borrower.email || "—",
        "DOB": form.borrower.dob ? new Date(form.borrower.dob).toLocaleDateString("en-IN") : "—",
        "PAN": form.borrower.pan,
        "Address": `${form.borrower.current_address}, ${form.borrower.city}, ${form.borrower.state} - ${form.borrower.pincode}`,
      }} />
      <ReviewBlock title="Income & employment" data={{
        "Type": form.employment.employment_type,
        "Employer": form.employment.employer_name,
        "Monthly income": formatINR(form.employment.monthly_income),
        "Family income": formatINR(form.employment.family_income),
        "Existing EMIs": formatINR(form.employment.existing_emi),
        "CIBIL (self-declared)": form.employment.cibil_score || "—",
      }} />

      <div className="space-y-3 mt-6">
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-md cursor-pointer" data-testid="consent-cibil">
          <Checkbox checked={consent1} onCheckedChange={(v) => setConsent1(!!v)} className="mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-slate-900">CIBIL / bureau pull consent</div>
            <div className="text-xs text-slate-600 mt-0.5">I authorize Red Fort Capital Finance NBFC to obtain my credit information from CIBIL / Experian / Equifax / CRIF for the purpose of evaluating this loan application.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-md cursor-pointer" data-testid="consent-policy">
          <Checkbox checked={consent2} onCheckedChange={(v) => setConsent2(!!v)} className="mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-slate-900">Credit policy consent</div>
            <div className="text-xs text-slate-600 mt-0.5">I understand that loan approval is subject to Red Fort Capital credit policy, vehicle valuation, document verification and final underwriting approval. The information above is true to the best of my knowledge.</div>
          </div>
        </label>
      </div>

      {(!consent1 || !consent2) && (
        <div className="text-xs text-amber-700 mt-3 flex items-center gap-1"><ShieldCheck size={14} /> Please accept both consents to enable submission.</div>
      )}
    </div>
  );
}

function ReviewBlock({ title, data }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm border border-slate-200 rounded-md p-4 bg-slate-50/40">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2">
            <div className="text-slate-500">{k}</div>
            <div className="text-slate-900 font-mono text-right">{v || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
